import React from 'react';

/**
 * CutterHealthCard
 * Professional Industrial IoT Health Monitoring Card for Fleet Cutters.
 * Displays real tool wear telemetry, remaining lifespan percentage, and machine distribution.
 */
const CutterHealthCard = ({ title, data, subtitle, ratedLife }) => {
  if (!data || data.percentage == null || isNaN(data.percentage)) {
    return null;
  }

  const {
    percentage = 0,
    healthy = 0,
    warning = 0,
    critical = 0,
    status
  } = data;

  const total = healthy + warning + critical || 1;
  const healthyPct = Math.round((healthy / total) * 100);
  const warningPct = Math.round((warning / total) * 100);
  const criticalPct = Math.max(0, 100 - healthyPct - warningPct);

  // Status badge styling
  let statusTheme = {
    badgeBg: 'bg-emerald-50',
    badgeText: 'text-emerald-700',
    badgeBorder: 'border-emerald-200',
    stroke: '#10b981',
    label: status || 'Optimal',
  };

  if (percentage < 50) {
    statusTheme = {
      badgeBg: 'bg-rose-50',
      badgeText: 'text-rose-700',
      badgeBorder: 'border-rose-200',
      stroke: '#ef4444',
      label: status || 'Critical',
    };
  } else if (percentage < 75) {
    statusTheme = {
      badgeBg: 'bg-amber-50',
      badgeText: 'text-amber-700',
      badgeBorder: 'border-amber-200',
      stroke: '#f59e0b',
      label: status || 'Warning',
    };
  } else if (percentage < 85) {
    statusTheme = {
      badgeBg: 'bg-blue-50',
      badgeText: 'text-blue-700',
      badgeBorder: 'border-blue-200',
      stroke: '#3b82f6',
      label: status || 'Good',
    };
  }


  const isFlat = title?.toLowerCase().includes('flat');
  const defaultRated = isFlat ? '5,000' : '70';
  const displayRated = ratedLife ? Number(ratedLife).toLocaleString() : defaultRated;

  return (
    <div className="bg-white w-full rounded-xl border border-slate-200/90 shadow-xs p-4 hover:shadow-sm transition-all flex flex-col justify-between h-full">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 shrink-0">
              {isFlat ? (
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="9"></circle>
                  <circle cx="12" cy="12" r="3"></circle>
                  <line x1="12" y1="3" x2="12" y2="6"></line>
                  <line x1="12" y1="18" x2="12" y2="21"></line>
                </svg>
              ) : (
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="12 2 15 8 9 8"></polygon>
                  <line x1="12" y1="8" x2="12" y2="22"></line>
                  <line x1="9" y1="14" x2="15" y2="14"></line>
                  <line x1="10" y1="18" x2="14" y2="18"></line>
                </svg>
              )}
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 text-xs sm:text-sm tracking-tight leading-tight">
                {title}
              </h3>
              <span className="text-[10px] text-slate-400 font-mono">
                Rated: {displayRated} cuts/tool
              </span>
            </div>
          </div>

          <span
            className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${statusTheme.badgeBg} ${statusTheme.badgeText} ${statusTheme.badgeBorder}`}
          >
            {statusTheme.label}
          </span>
        </div>

        {/* Gauge & Machine Breakdown */}
        <div className="flex items-center gap-3.5 my-2">
          {/* Circular Progress Gauge */}
          <div className="relative flex items-center justify-center shrink-0 w-36 h-20">
            
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-2xl font-bold font-mono text-slate-900 leading-none">
                {percentage}%
              </span>
              <span className="text-[9px] text-slate-400 font-medium uppercase tracking-wider mt-0.5">
                Life
              </span>
            </div>
          </div>

          {/* Machine Condition Breakdown */}
          <div className="flex-1 space-y-1 text-xs">
            <div className="flex items-center justify-between py-1 px-2 rounded-md bg-emerald-50/70 border border-emerald-100">
              <span className="flex items-center gap-1.5 text-slate-700 font-medium text-[11px]">
                <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0"></span>
                Healthy (&gt;75%)
              </span>
              <span className="font-bold font-mono text-slate-900">{healthy}</span>
            </div>

            <div className="flex items-center justify-between py-1 px-2 rounded-md bg-amber-50/70 border border-amber-100">
              <span className="flex items-center gap-1.5 text-slate-700 font-medium text-[11px]">
                <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0"></span>
                Warning (50-75%)
              </span>
              <span className="font-bold font-mono text-slate-900">{warning}</span>
            </div>

            <div className="flex items-center justify-between py-1 px-2 rounded-md bg-rose-50/70 border border-rose-100">
              <span className="flex items-center gap-1.5 text-slate-700 font-medium text-[11px]">
                <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0"></span>
                Critical (&lt;50%)
              </span>
              <span className="font-bold font-mono text-slate-900">{critical}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Segmented Distribution Bar */}
      <div className="pt-2.5 border-t border-slate-100 mt-2">
        <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1 font-medium">
          <span>Fleet Distribution</span>
          <span className="font-mono">{total} machines active</span>
        </div>
        <div className="w-full h-1.5 rounded-full bg-slate-100 overflow-hidden flex">
          {healthy > 0 && (
            <div
              style={{ width: `${healthyPct}%` }}
              className="bg-emerald-500 transition-all duration-500"
              title={`Healthy: ${healthy} (${healthyPct}%)`}
            />
          )}
          {warning > 0 && (
            <div
              style={{ width: `${warningPct}%` }}
              className="bg-amber-500 transition-all duration-500"
              title={`Warning: ${warning} (${warningPct}%)`}
            />
          )}
          {critical > 0 && (
            <div
              style={{ width: `${criticalPct}%` }}
              className="bg-rose-500 transition-all duration-500"
              title={`Critical: ${critical} (${criticalPct}%)`}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CutterHealthCard;
